import React, { useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Chip, IconButton, Tabs, Tab, TextField, Select,
  MenuItem, FormControl, InputLabel, Radio, RadioGroup, FormControlLabel,
  FormLabel, Switch, Button, Divider, Table, TableHead, TableRow, TableCell,
  TableBody, Tooltip, LinearProgress, Alert, Checkbox,
} from '@mui/material';
import {
  PlayArrow as PlayIcon, SkipNext as StepIcon, Pause as PauseIcon,
  Stop as StopIcon, FiberManualRecord as RecordIcon, Add as AddIcon,
  CheckCircle as PassIcon, Cancel as FailIcon, HourglassEmpty as RunningIcon,
  Circle as PendingIcon, FiberManualRecord as DotIcon, DragIndicator as DragIcon,
  ExpandMore as ExpandMoreIcon, ChevronRight as ChevronRightIcon,
  Delete as DeleteIcon, Undo as UndoIcon, Save as SaveIcon,
  ArrowUpward as UpIcon, ArrowDownward as DownIcon, Close as CloseIcon,
} from '@mui/icons-material';

const KHAN_STEPS = [
  { num: 1, type: 'navigate', name: 'Navigate to Khan Academy', status: 'passed', selector: '', value: 'https://www.khanacademy.org/login', timeout: 10000 },
  { num: 2, type: 'wait', name: 'Wait for login form', status: 'passed', selector: '#uid-identifier-input', value: '', timeout: 5000 },
  { num: 3, type: 'type', name: 'Enter email/username', status: 'running', selector: '#uid-identifier-input', value: '{{credential.username}}', timeout: 3000 },
  { num: 4, type: 'click', name: 'Click Continue button', status: 'pending', selector: 'button[data-test-id="identifier-submit"]', value: '', timeout: 3000 },
  { num: 5, type: 'wait', name: 'Wait for password field', status: 'pending', selector: '#uid-password-input', value: '', timeout: 5000 },
  { num: 6, type: 'type', name: 'Enter password', status: 'pending', selector: '#uid-password-input', value: '{{credential.password}}', timeout: 3000 },
  { num: 7, type: 'assert', name: 'Verify login success', status: 'pending', selector: '[data-test-id="user-menu"]', value: 'exists', timeout: 10000 },
];

const STEP_TYPES = [
  { value: 'navigate', label: 'Navigate' },
  { value: 'type', label: 'Type' },
  { value: 'click', label: 'Click' },
  { value: 'wait', label: 'Wait (Selector)' },
  { value: 'wait_nav', label: 'Wait (Navigation)' },
  { value: 'assert', label: 'Assert URL' },
  { value: 'assert_element', label: 'Assert Element' },
  { value: 'script', label: 'Script' },
  { value: 'select', label: 'Select' },
];

const FAILURE_OPTIONS = [
  { value: 'stop', label: 'Stop execution' },
  { value: 'skip', label: 'Skip this step and continue' },
  { value: 'jump', label: 'Jump to step...' },
  { value: 'retry_stop', label: 'Retry N times then stop' },
  { value: 'retry_skip', label: 'Retry N times then skip' },
];

const TYPE_CHIP_COLORS = {
  navigate: { bg: '#e3f2fd', color: '#1565c0' },
  wait: { bg: '#fff3e0', color: '#e65100' },
  wait_nav: { bg: '#fff3e0', color: '#e65100' },
  type: { bg: '#e3f2fd', color: '#1565c0' },
  click: { bg: '#e3f2fd', color: '#1565c0' },
  assert: { bg: '#e8f5e9', color: '#2e7d32' },
  assert_element: { bg: '#e8f5e9', color: '#2e7d32' },
  script: { bg: '#f3e5f5', color: '#6a1b9a' },
  select: { bg: '#e3f2fd', color: '#1565c0' },
};

const STATUS_CONFIGS = {
  passed: { icon: <PassIcon sx={{ fontSize: 18, color: '#4caf50' }} />, bg: '#f1f8e9' },
  running: { icon: <RunningIcon sx={{ fontSize: 18, color: '#ff9800' }} />, bg: '#fff8e1', border: '2px solid #ffc107' },
  pending: { icon: <PendingIcon sx={{ fontSize: 14, color: '#bdbdbd' }} />, bg: 'transparent' },
};

const MOCK_EXECUTIONS = [
  { id: 101, date: '2026-04-09 08:32', status: 'passed', duration: '4.2s', credential: 'student.jmart @ fivekeys', steps: '7/7' },
  { id: 102, date: '2026-04-08 14:15', status: 'passed', duration: '3.8s', credential: 'test1user2 @ fivekeys', steps: '7/7' },
  { id: 103, date: '2026-04-07 09:01', status: 'failed', duration: '12.1s', credential: 'test5user1 @ riverside', steps: '3/7' },
  { id: 104, date: '2026-04-06 16:45', status: 'passed', duration: '4.0s', credential: 'demo_student @ sandi', steps: '7/7' },
  { id: 105, date: '2026-04-05 11:20', status: 'passed', duration: '3.9s', credential: 'test2user4 @ fivekeys', steps: '7/7' },
];

const MOCK_OUTPUT = {
  scriptId: 1,
  scriptName: 'Khan Academy Login',
  executionId: 'exec-20260409-0832',
  status: 'running',
  startedAt: '2026-04-09T08:32:14.000Z',
  credential: { username: 'student.jmart', client: 'fivekeys' },
  stepsCompleted: 2,
  stepsTotal: 7,
  currentStep: { num: 3, name: 'Enter email/username', elapsed: '2.1s' },
  logs: [
    { ts: '08:32:14.102', level: 'info', msg: 'Navigated to https://www.khanacademy.org/login' },
    { ts: '08:32:15.843', level: 'info', msg: 'Element found: #uid-identifier-input' },
    { ts: '08:32:16.201', level: 'info', msg: 'Typing into #uid-identifier-input...' },
  ],
};

const TabPanel = ({ children, value, index, ...rest }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ p: 0 }} {...rest}>
    {value === index && children}
  </Box>
);

/* --- Reusable step editing fields by type --- */

const SelectorField = ({ value }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Selector</Typography>
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      <TextField size="small" fullWidth value={value} variant="outlined"
        sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }} />
      <Tooltip title="Highlight element on page"><IconButton size="small" sx={{ bgcolor: '#e3f2fd', fontSize: '0.9rem' }}>&#127919;</IconButton></Tooltip>
      <Tooltip title="Pick element from page"><IconButton size="small" sx={{ bgcolor: '#e3f2fd', fontSize: '0.9rem' }}>&#128204;</IconButton></Tooltip>
    </Box>
  </Box>
);

const ValueFieldWithCred = ({ value, label = 'Value' }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>{label}</Typography>
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      <TextField size="small" fullWidth value={value} variant="outlined"
        sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }} />
      <Tooltip title="Insert {{credential.username}}">
        <Button size="small" variant="outlined" sx={{ minWidth: 0, px: 0.8, fontSize: '0.7rem', whiteSpace: 'nowrap' }}>{'{{user}}'}</Button>
      </Tooltip>
      <Tooltip title="Insert {{credential.password}}">
        <Button size="small" variant="outlined" sx={{ minWidth: 0, px: 0.8, fontSize: '0.7rem', whiteSpace: 'nowrap' }}>{'{{pass}}'}</Button>
      </Tooltip>
    </Box>
  </Box>
);

const TimeoutField = ({ value }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Timeout (ms)</Typography>
    <TextField size="small" value={value} type="number" variant="outlined"
      sx={{ width: 120, '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }} />
  </Box>
);

const FailureHandlingField = ({ steps }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>On Failure</Typography>
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <Select value="stop" variant="outlined" sx={{ fontSize: '0.8rem' }}>
          {FAILURE_OPTIONS.map(opt => (
            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.8rem' }}>{opt.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  </Box>
);

const StepTypeFields = ({ step, steps }) => {
  const t = step.type;
  switch (t) {
    case 'navigate':
      return (
        <>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>URL</Typography>
            <TextField size="small" fullWidth value={step.value || ''} variant="outlined"
              sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }} />
          </Box>
          <TimeoutField value={step.timeout} />
          <FailureHandlingField steps={steps} />
        </>
      );
    case 'type':
      return (
        <>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Name</Typography>
            <TextField size="small" fullWidth value={step.name} variant="outlined" sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }} />
          </Box>
          <SelectorField value={step.selector} />
          <ValueFieldWithCred value={step.value} />
          <FormControlLabel
            control={<Checkbox size="small" />}
            label={<Typography variant="caption">Clear field first</Typography>}
            sx={{ mb: 1 }}
          />
          <TimeoutField value={step.timeout} />
          <FailureHandlingField steps={steps} />
        </>
      );
    case 'click':
      return (
        <>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Name</Typography>
            <TextField size="small" fullWidth value={step.name} variant="outlined" sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }} />
          </Box>
          <SelectorField value={step.selector} />
          <TimeoutField value={step.timeout} />
          <FailureHandlingField steps={steps} />
        </>
      );
    case 'wait':
      return (
        <>
          <SelectorField value={step.selector} />
          <TimeoutField value={step.timeout} />
          <FailureHandlingField steps={steps} />
        </>
      );
    case 'wait_nav':
      return (
        <>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Expected URL (optional)</Typography>
            <TextField size="small" fullWidth value={step.value || ''} variant="outlined"
              sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }} />
          </Box>
          <TimeoutField value={step.timeout} />
          <FailureHandlingField steps={steps} />
        </>
      );
    case 'assert':
      return (
        <>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Operator</Typography>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <Select value="contains" variant="outlined" sx={{ fontSize: '0.8rem' }}>
                <MenuItem value="contains" sx={{ fontSize: '0.8rem' }}>contains</MenuItem>
                <MenuItem value="equals" sx={{ fontSize: '0.8rem' }}>equals</MenuItem>
                <MenuItem value="startsWith" sx={{ fontSize: '0.8rem' }}>starts with</MenuItem>
                <MenuItem value="matches" sx={{ fontSize: '0.8rem' }}>matches (regex)</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Expected URL / Value</Typography>
            <TextField size="small" fullWidth value={step.value || step.selector || ''} variant="outlined"
              sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }} />
          </Box>
          <TimeoutField value={step.timeout} />
          <FailureHandlingField steps={steps} />
        </>
      );
    case 'assert_element':
      return (
        <>
          <SelectorField value={step.selector} />
          <TimeoutField value={step.timeout} />
          <FailureHandlingField steps={steps} />
        </>
      );
    case 'script':
      return (
        <>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Script Body</Typography>
            <TextField size="small" fullWidth multiline rows={4} value={step.value || ''} variant="outlined"
              sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem' } }} />
          </Box>
          <TimeoutField value={step.timeout} />
          <FailureHandlingField steps={steps} />
        </>
      );
    case 'select':
      return (
        <>
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Name</Typography>
            <TextField size="small" fullWidth value={step.name} variant="outlined" sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' } }} />
          </Box>
          <SelectorField value={step.selector} />
          <ValueFieldWithCred value={step.value} label="Value" />
          <TimeoutField value={step.timeout} />
          <FailureHandlingField steps={steps} />
        </>
      );
    default:
      return (
        <>
          {step.selector && <SelectorField value={step.selector} />}
          {step.value && <ValueFieldWithCred value={step.value} />}
          <TimeoutField value={step.timeout} />
          <FailureHandlingField steps={steps} />
        </>
      );
  }
};

const WatchModeMockup = () => {
  const [rightTab, setRightTab] = useState(0);
  const [expandedStep, setExpandedStep] = useState(null);
  const [dividerPos, setDividerPos] = useState(55);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(true); // Show the bar in mockup
  const containerRef = useRef(null);
  const draggingRef = useRef(false);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    draggingRef.current = true;
    const handleMouseMove = (moveEvent) => {
      if (!draggingRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const newPos = ((moveEvent.clientX - rect.left) / rect.width) * 100;
      if (newPos >= 25 && newPos <= 75) {
        setDividerPos(newPos);
      }
    };
    const handleMouseUp = () => {
      draggingRef.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  return (
    <Box ref={containerRef} sx={{ display: 'flex', height: 'calc(100vh - 180px)', gap: 0, userSelect: draggingRef.current ? 'none' : 'auto' }}>
      {/* LEFT PANEL - CDP Screencast */}
      <Paper sx={{ width: `${dividerPos}%`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }} elevation={2}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, borderBottom: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
          <DotIcon sx={{ color: '#4caf50', fontSize: 14, mr: 1 }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#4caf50', mr: 2 }}>Live Server Browser</Typography>
          <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1 }}>Khan Academy Login</Typography>
          <Chip label="00:06.3" size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
        </Box>

        {/* CDP Screencast placeholder */}
        <Box sx={{
          flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          bgcolor: '#1a1a2e', color: '#8892b0', position: 'relative', overflow: 'hidden',
        }}>
          {/* Fake browser chrome */}
          <Box sx={{
            position: 'absolute', top: 0, left: 0, right: 0, height: 36,
            bgcolor: '#2d2d44', display: 'flex', alignItems: 'center', px: 1.5, gap: 1,
          }}>
            <Box sx={{ display: 'flex', gap: 0.6 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ff5f57' }} />
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ffbd2e' }} />
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#28c840' }} />
            </Box>
            <Box sx={{
              flexGrow: 1, bgcolor: '#1a1a2e', borderRadius: 1, px: 1.5, py: 0.3,
              fontSize: '0.75rem', color: '#8892b0', fontFamily: 'monospace',
            }}>
              https://www.khanacademy.org/login
            </Box>
          </Box>

          {/* Fake page content */}
          <Box sx={{ mt: 4, textAlign: 'center', px: 3 }}>
            <Box sx={{
              width: 300, mx: 'auto', p: 4, border: '1px solid #3d3d5c',
              borderRadius: 2, bgcolor: '#16213e',
            }}>
              <Typography variant="h6" sx={{ color: '#ccd6f6', mb: 3 }}>Sign in</Typography>
              {/* Element highlight overlay for step 3 target */}
              <Tooltip
                title="Step 3 target: input#uid-identifier-input"
                placement="right"
                open={expandedStep === 3}
                arrow
              >
                <Box sx={{
                  border: expandedStep === 3 ? '2px solid #42a5f5' : '2px solid #ffc107',
                  borderRadius: 1, p: 1.5, mb: 2, bgcolor: '#1a1a2e',
                  display: 'flex', alignItems: 'center',
                  position: 'relative',
                  boxShadow: expandedStep === 3 ? '0 0 12px rgba(66, 165, 245, 0.4), inset 0 0 8px rgba(66, 165, 245, 0.15)' : 'none',
                }}>
                  {expandedStep === 3 && (
                    <Box sx={{
                      position: 'absolute', top: -20, left: 0, bgcolor: 'rgba(66, 165, 245, 0.85)',
                      color: '#fff', px: 1, py: 0.2, borderRadius: '4px 4px 0 0',
                      fontSize: '0.6rem', fontFamily: 'monospace',
                    }}>
                      #uid-identifier-input
                    </Box>
                  )}
                  <Typography variant="body2" sx={{ color: '#ccd6f6', fontFamily: 'monospace' }}>
                    student.jmart|
                  </Typography>
                </Box>
              </Tooltip>
              <Typography variant="caption" sx={{ color: '#ffc107', display: 'block', mb: 2 }}>
                Currently typing...
              </Typography>
              <Box sx={{
                bgcolor: '#1976d2', borderRadius: 1, p: 1, textAlign: 'center',
                color: '#fff', opacity: 0.5,
              }}>
                Continue
              </Box>
            </Box>
            <Typography variant="caption" sx={{ color: '#4a5568', mt: 3, display: 'block' }}>
              CDP Screencast Stream (Headless Chrome via Playwright)
            </Typography>
          </Box>
        </Box>

        {/* Bottom status bar */}
        <Box sx={{
          display: 'flex', alignItems: 'center', px: 2, py: 1,
          borderTop: '1px solid #e0e0e0', bgcolor: '#fff8e1',
        }}>
          <RunningIcon sx={{ fontSize: 16, color: '#ff9800', mr: 1 }} />
          <Typography variant="body2" fontWeight={600} sx={{ color: '#e65100' }}>
            Step 3/7: Enter email/username
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            2.1s elapsed
          </Typography>
        </Box>
      </Paper>

      {/* DIVIDER */}
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          width: 8, cursor: 'col-resize', display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: draggingRef.current ? '#1976d2' : '#e0e0e0',
          '&:hover': { bgcolor: '#bbdefb' },
          transition: 'background-color 0.15s',
          flexShrink: 0,
        }}
      >
        <DragIcon sx={{ fontSize: 16, color: '#757575', transform: 'rotate(90deg)' }} />
      </Box>

      {/* RIGHT PANEL - Editor */}
      <Paper sx={{ width: `${100 - dividerPos}%`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }} elevation={2}>
        <Tabs value={rightTab} onChange={(_, v) => setRightTab(v)} variant="scrollable" scrollButtons="auto"
          sx={{ borderBottom: '1px solid #e0e0e0', minHeight: 42, '& .MuiTab-root': { minHeight: 42, textTransform: 'none', fontSize: '0.85rem' } }}
        >
          <Tab label="Steps" />
          <Tab label="Variables" />
          <Tab label="Settings" />
          <Tab label="History" />
          <Tab label="Output" />
        </Tabs>

        <Box sx={{ flexGrow: 1, overflow: 'auto', pb: rightTab === 0 && hasUnsavedChanges ? '64px' : 0 }}>
          {/* STEPS TAB */}
          <TabPanel value={rightTab} index={0}>
            {/* Transport bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5, borderBottom: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
              <Tooltip title="Run All"><IconButton size="small" sx={{ bgcolor: '#4caf50', color: '#fff', '&:hover': { bgcolor: '#388e3c' } }}><PlayIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Step Forward"><IconButton size="small" sx={{ bgcolor: '#1976d2', color: '#fff', '&:hover': { bgcolor: '#1565c0' } }}><StepIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Pause"><IconButton size="small"><PauseIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Stop"><IconButton size="small"><StopIcon fontSize="small" /></IconButton></Tooltip>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <Tooltip title="Record Steps"><IconButton size="small" sx={{ color: '#f44336' }}><RecordIcon fontSize="small" /></IconButton></Tooltip>
              <Box sx={{ flexGrow: 1 }} />
              <LinearProgress variant="determinate" value={2/7*100} sx={{ width: 80, mr: 1 }} />
              <Typography variant="caption" color="text.secondary">2/7</Typography>
            </Box>

            {/* Credential dropdown */}
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Credential</InputLabel>
                <Select value="cred1" label="Credential">
                  <MenuItem value="cred1">Khan Academy - student.jmart @ fivekeys</MenuItem>
                  <MenuItem value="cred2">Khan Academy - test1user2 @ fivekeys</MenuItem>
                  <MenuItem value="cred3">Khan Academy - demo_student @ sandi</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Step list */}
            <Box sx={{ px: 1, py: 1 }}>
              {KHAN_STEPS.map((step, idx) => {
                const sc = STATUS_CONFIGS[step.status];
                const tc = TYPE_CHIP_COLORS[step.type] || TYPE_CHIP_COLORS.click;
                const isExp = expandedStep === step.num;
                return (
                  <Box key={step.num}>
                    <Box
                      onClick={() => setExpandedStep(isExp ? null : step.num)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
                        borderRadius: 1, cursor: 'pointer', mb: 0.5,
                        bgcolor: sc.bg,
                        border: sc.border || '1px solid transparent',
                        '&:hover': { bgcolor: step.status === 'pending' ? '#f5f5f5' : sc.bg },
                      }}
                    >
                      {/* Drag handle */}
                      <Box sx={{ cursor: 'grab', display: 'flex', alignItems: 'center', color: '#9e9e9e', '&:hover': { color: '#616161' }, mr: -0.5 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DragIcon sx={{ fontSize: 18 }} />
                      </Box>
                      {sc.icon}
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#757575', minWidth: 20 }}>
                        {step.num}
                      </Typography>
                      <Chip
                        label={step.type}
                        size="small"
                        sx={{ bgcolor: tc.bg, color: tc.color, fontWeight: 600, fontSize: '0.65rem', height: 20, textTransform: 'capitalize' }}
                      />
                      <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: step.status === 'running' ? 700 : 400 }}>
                        {step.name}
                      </Typography>
                      {isExp ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                    </Box>
                    {isExp && (
                      <Box sx={{ ml: 5, mb: 1, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                        {/* Step type chooser */}
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>Step Type</Typography>
                          <FormControl size="small" sx={{ minWidth: 180 }}>
                            <Select value={step.type} variant="outlined" sx={{ fontSize: '0.85rem' }}>
                              {STEP_TYPES.map(t => (
                                <MenuItem key={t.value} value={t.value} sx={{ fontSize: '0.85rem' }}>{t.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>

                        {/* Type-specific fields */}
                        <StepTypeFields step={step} steps={KHAN_STEPS} />

                        {/* Action buttons: Up, Down, Run, Delete */}
                        <Divider sx={{ my: 1.5 }} />
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Tooltip title="Move step up">
                            <span>
                              <IconButton size="small" disabled={idx === 0}>
                                <UpIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Move step down">
                            <span>
                              <IconButton size="small" disabled={idx >= KHAN_STEPS.length - 1}>
                                <DownIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Run this step only">
                            <IconButton size="small" sx={{ color: '#4caf50' }}>
                              <PlayIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Box sx={{ flexGrow: 1 }} />
                          <Tooltip title="Delete step">
                            <IconButton size="small" sx={{ color: '#f44336' }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })}

              <Button startIcon={<AddIcon />} size="small" sx={{ mt: 1, ml: 1 }} variant="outlined">
                Add Step
              </Button>
            </Box>
          </TabPanel>

          {/* VARIABLES TAB */}
          <TabPanel value={rightTab} index={1}>
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Script Variables</Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>Fill all variables before running</Alert>

              {/* Existing variables as editable rows */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5 }}>
                  <TextField label="Variable name" size="small" value="class_id" sx={{ flex: 1 }} />
                  <TextField label="Value" size="small" placeholder="e.g. 5th_period_math" sx={{ flex: 1.5 }} />
                  <Tooltip title="Remove variable">
                    <IconButton size="small" sx={{ mt: 0.5, color: '#f44336' }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5 }}>
                  <TextField label="Variable name" size="small" value="student_name" sx={{ flex: 1 }} />
                  <TextField label="Value" size="small" placeholder="e.g. John Smith" sx={{ flex: 1.5 }} />
                  <Tooltip title="Remove variable">
                    <IconButton size="small" sx={{ mt: 0.5, color: '#f44336' }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Button startIcon={<AddIcon />} size="small" variant="outlined" sx={{ mb: 2 }}>
                Add Variable
              </Button>

              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" color="text.secondary">
                Variables are injected into step values using {'{{variable_name}}'} syntax.
                Credential fields (username, password) are auto-injected from the selected credential.
              </Typography>
            </Box>
          </TabPanel>

          {/* SETTINGS TAB */}
          <TabPanel value={rightTab} index={2}>
            <Box sx={{ p: 2 }}>
              <FormControl component="fieldset" sx={{ mb: 3 }}>
                <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>Execution Mode</FormLabel>
                <RadioGroup row value="backend">
                  <FormControlLabel value="frontend" control={<Radio size="small" />} label="Frontend" />
                  <FormControlLabel value="backend" control={<Radio size="small" />} label="Backend" />
                  <FormControlLabel value="both" control={<Radio size="small" />} label="Both" />
                </RadioGroup>
              </FormControl>

              <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                <InputLabel>Script Type</InputLabel>
                <Select value="login" label="Script Type">
                  <MenuItem value="login">Login</MenuItem>
                  <MenuItem value="progress">Progress Collection</MenuItem>
                  <MenuItem value="roster">Roster</MenuItem>
                  <MenuItem value="de-roster">De-roster</MenuItem>
                  <MenuItem value="account-creation">Account Creation</MenuItem>
                </Select>
              </FormControl>

              <TextField label="App Key" size="small" fullWidth value="khan" sx={{ mb: 3 }} InputProps={{ readOnly: true }} />

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Chaining</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Prerequisite scripts that must run first:</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                <Chip label="Khan Academy Login" onDelete={() => {}} size="small" color="primary" variant="outlined" />
                <Button size="small" startIcon={<AddIcon />}>Add</Button>
              </Box>

              <Divider sx={{ my: 2 }} />

              <FormControlLabel control={<Switch size="small" />} label="Teacher Mode" sx={{ mb: 2, display: 'block' }} />

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Schedule</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">Not scheduled</Typography>
                <Button size="small" variant="outlined">Enable Schedule</Button>
              </Box>
            </Box>
          </TabPanel>

          {/* HISTORY TAB */}
          <TabPanel value={rightTab} index={3}>
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Execution History</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Credential</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Steps</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {MOCK_EXECUTIONS.map(exec => (
                    <TableRow key={exec.id}>
                      <TableCell><Typography variant="caption">{exec.date}</Typography></TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {exec.status === 'passed' ? <PassIcon sx={{ color: '#4caf50', fontSize: 16 }} /> : <FailIcon sx={{ color: '#f44336', fontSize: 16 }} />}
                          <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>{exec.status}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell><Typography variant="caption">{exec.duration}</Typography></TableCell>
                      <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{exec.credential}</Typography></TableCell>
                      <TableCell><Typography variant="caption">{exec.steps}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </TabPanel>

          {/* OUTPUT TAB */}
          <TabPanel value={rightTab} index={4}>
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Execution Output</Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#1a1a2e', borderRadius: 1, overflow: 'auto', maxHeight: 500 }}>
                <pre style={{ color: '#a5d6a7', fontFamily: 'monospace', fontSize: '0.8rem', margin: 0, whiteSpace: 'pre-wrap' }}>
{JSON.stringify(MOCK_OUTPUT, null, 2)}
                </pre>
              </Paper>
            </Box>
          </TabPanel>
        </Box>

        {/* Save/Cancel/Undo sticky bar - only on Steps tab with unsaved changes */}
        {rightTab === 0 && hasUnsavedChanges && (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5,
            borderTop: '2px solid #ffc107', bgcolor: '#fffde7',
            position: 'sticky', bottom: 0, zIndex: 10,
          }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ffc107', flexShrink: 0 }} />
            <Typography variant="caption" sx={{ color: '#f57f17', fontWeight: 700, mr: 1 }}>Unsaved changes</Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Tooltip title="Undo last change">
              <IconButton size="small"><UndoIcon fontSize="small" /></IconButton>
            </Tooltip>
            <Button size="small" variant="outlined" color="inherit" sx={{ textTransform: 'none', fontSize: '0.8rem' }}>
              Discard Changes
            </Button>
            <Button size="small" variant="contained" startIcon={<SaveIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', fontSize: '0.8rem', bgcolor: '#1976d2' }}
            >
              Save Script
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default WatchModeMockup;
